import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_URL;

const getHeaders = (isFormData = false) => ({
  "Content-Type": isFormData ? "multipart/form-data" : "application/json",
  Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
});

export const fetchEmployeePdfs = createAsyncThunk(
  "employeePdfs/fetchEmployeePdfs",
  async (params, { rejectWithValue }) => {
    try {
      const organizationId =
        typeof params === "object" ? params.organizationId : params;
      const isManagement = typeof params === "object" && params.management;
      const page = isManagement ? params.page || 1 : undefined;
      const limit = isManagement ? params.limit || 10 : undefined;
      const search = isManagement ? params.search || "" : undefined;
      const endpoint = isManagement
        ? `${BASE_URL}/employee-pdfs/${organizationId}/organization/management`
        : `${BASE_URL}/employee-pdfs/${organizationId}/organization`;

      const res = await axios.get(endpoint, {
        headers: getHeaders(),
        params: isManagement
          ? {
              page,
              limit,
              search,
            }
          : undefined,
      });

      if (Array.isArray(res.data?.data)) {
        return {
          ...res.data,
          data: res.data.data,
        };
      }

      return res.data?.data ?? res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

export const uploadEmployeePdfs = createAsyncThunk(
  "employeePdfs/uploadEmployeePdfs",
  async (data, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${BASE_URL}/employee-pdfs`, data, {
        headers: getHeaders(true),
      });

      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

export const deleteEmployeePdf = createAsyncThunk(
  "employeePdfs/deleteEmployeePdf",
  async (params, { rejectWithValue }) => {
    try {
      const id = typeof params === "object" ? params.id : params;
      const organizationId =
        typeof params === "object" ? params.organizationId : undefined;

      await axios.delete(`${BASE_URL}/employee-pdfs/${id}`, {
        headers: getHeaders(),
        params: organizationId
          ? {
              organization_id: organizationId,
            }
          : undefined,
      });

      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

const employeePdfSlice = createSlice({
  name: "employeePdfs",
  initialState: {
    pdfs: [],
    total: 0,
    loading: false,
    saving: false,
    deleting: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmployeePdfs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployeePdfs.fulfilled, (state, action) => {
        state.loading = false;
        state.pdfs = Array.isArray(action.payload)
          ? action.payload
          : action.payload?.pdfs ||
            action.payload?.data ||
            action.payload?.employee_pdfs ||
            [];
        state.total =
          action.payload?.total ||
          action.payload?.total_count ||
          action.payload?.total_records ||
          action.payload?.count ||
          action.payload?.pagination?.total ||
          action.payload?.pagination?.total_count ||
          state.pdfs.length;
      })
      .addCase(fetchEmployeePdfs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })


      .addCase(uploadEmployeePdfs.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(uploadEmployeePdfs.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(uploadEmployeePdfs.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      
      .addCase(deleteEmployeePdf.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteEmployeePdf.fulfilled, (state, action) => {
        state.deleting = false;
        state.pdfs = state.pdfs.filter((item) => item.id !== action.payload);
      })
      .addCase(deleteEmployeePdf.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.payload;
      });
  },
});

export default employeePdfSlice.reducer;
